import { supabaseAuthProvider } from 'ra-supabase';
import { AuthProvider, UserIdentity } from 'react-admin';
import { supabase } from './supabase';
import { canAccess } from '../commons/canAccess';
import type { Sale, SignUpData } from '../../types';
import { dataProvider } from './dataProvider';

const baseAuthProvider = supabaseAuthProvider(supabase, {
    getIdentity: async (): Promise<UserIdentity> => {
        const sale: Sale | null | undefined = await getSaleFromCache();

        if (sale == null) {
            const {
                data: { user },
            } = await supabase.auth.getUser();
            if (user) {
                console.warn(
                    '[AuthProvider] getIdentity: No sale record, returning minimal identity from auth user.'
                );
                return {
                    id: user.id,
                    fullName: user.email,
                    approved: false,
                    administrator: false,
                    email: user.email,
                    first_name: user.user_metadata?.first_name,
                    last_name: user.user_metadata?.last_name,
                };
            }
            console.error(
                '[AuthProvider] getIdentity: No sale record and no auth user session. Throwing error.'
            );
            throw new Error(
                'User profile data not found and no active session.'
            );
        }

        const identity: UserIdentity = {
            id: sale.id,
            fullName: `${sale.first_name} ${sale.last_name}`,
            avatar: (sale.avatar as any)?.src,
            first_name: sale.first_name,
            last_name: sale.last_name,
            email: sale.email,
            company_name: sale.company_name,
            agent_number: sale.agent_number,
            company_logo: sale.company_logo,
            administrator: sale.administrator,
            approved: (sale as any).approved ?? false,
        };
        console.log(
            '[AuthProvider] getIdentity (with approved): Returning identity:',
            identity
        );
        return identity;
    },
});

export async function getIsInitialized() {
    if (getIsInitialized._is_initialized_cache == null) {
        const { data } = await supabase
            .from('init_state')
            .select('is_initialized');

        getIsInitialized._is_initialized_cache =
            data?.at(0)?.is_initialized > 0;
    }

    return getIsInitialized._is_initialized_cache;
}

export namespace getIsInitialized {
    export var _is_initialized_cache: boolean | null = null;
}

interface CrmAuthProvider extends AuthProvider {
    registerUserAndProfile?: (data: Partial<Sale> & SignUpData) => Promise<any>;
}

export const authProvider: CrmAuthProvider = {
    ...baseAuthProvider,
    login: async params => {
        console.log(
            '🚪 [AuthProvider] login: ENTRY POINT - Attempting login with params:',
            params
        );

        try {
            await baseAuthProvider.login(params);
            console.log(
                '🚪 [AuthProvider] login: Supabase authentication successful.'
            );
        } catch (loginError) {
            console.log(
                '🚪 [AuthProvider] login: Supabase authentication FAILED:',
                loginError
            );
            throw loginError;
        }

        const {
            data: { user },
            error: userError,
        } = await supabase.auth.getUser();

        if (userError || !user) {
            console.error(
                '🚪 [AuthProvider] login: Error fetching user after Supabase auth or no user found:',
                userError
            );
            await baseAuthProvider.logout?.({});
            cachedSale = undefined;
            throw new Error(
                'Login failed: Could not retrieve user details after authentication.'
            );
        }
        console.log('🚪 [AuthProvider] login: Fetched auth user ID:', user.id);

        const sale: Sale | null | undefined = await getSaleFromCache();
        console.log(
            '🚪 [AuthProvider] login: Fetched sale record for approval check:',
            sale
        );

        if (!sale) {
            console.error(
                `🚪 [AuthProvider] login: No sales record found for authenticated user ${user.id}. Denying login.`
            );
            await baseAuthProvider.logout?.({});
            cachedSale = undefined;
            throw new Error(
                'Login failed: User profile not found. Please contact support.'
            );
        }

        if ((sale as any).approved === false) {
            console.warn(
                `🚪 [AuthProvider] login: User ${user.id} is not approved. Denying login.`
            );
            await baseAuthProvider.logout?.({});
            cachedSale = undefined;
            throw new Error(
                'Your account is pending administrator approval. You will be notified via email once it is active.'
            );
        }

        console.log(
            `🚪 [AuthProvider] login: User ${user.id} is approved. Checking MFA status...`
        );

        // Check MFA assurance level
        const { data: aalData } =
            await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

        if (
            aalData &&
            aalData.currentLevel === 'aal1' &&
            aalData.nextLevel === 'aal2'
        ) {
            // User has enrolled factors — needs to verify
            console.log(
                '🚪 [AuthProvider] login: MFA enrolled, redirecting to verify.'
            );
            cachedSale = undefined;
            return { redirectTo: '/mfa-verify' };
        }

        // Increment sign_in_count for MFA grace period tracking
        const currentCount = (sale as any).sign_in_count ?? 0;
        await supabase
            .from('sales')
            .update({ sign_in_count: currentCount + 1 })
            .eq('id', sale.id);

        // If this is NOT the first sign-in and user has no MFA enrolled,
        // redirect to enrollment
        if (
            currentCount > 0 &&
            aalData &&
            aalData.currentLevel === 'aal1' &&
            aalData.nextLevel === 'aal1'
        ) {
            console.log(
                '🚪 [AuthProvider] login: Not first sign-in and no MFA enrolled, redirecting to enroll.'
            );
            cachedSale = undefined;
            return { redirectTo: '/mfa-enroll' };
        }

        console.log(
            `🚪 [AuthProvider] login: User ${user.id} login complete (sign_in_count: ${currentCount + 1}).`
        );
        cachedSale = undefined;
        return Promise.resolve();
    },
    checkAuth: async params => {
        // Users are on the set-password page, nothing to do
        if (
            window.location.pathname === '/set-password' ||
            window.location.hash.includes('#/set-password')
        ) {
            return;
        }
        // Users are on the forgot-password page, nothing to do
        if (
            window.location.pathname === '/forgot-password' ||
            window.location.hash.includes('#/forgot-password')
        ) {
            return;
        }
        // Users are on the sign-up page, nothing to do
        if (
            window.location.pathname === '/sign-up' ||
            window.location.hash.includes('#/sign-up')
        ) {
            return;
        }
        // Users are on MFA pages — let them through at aal1
        if (
            window.location.pathname === '/mfa-verify' ||
            window.location.hash.includes('#/mfa-verify')
        ) {
            return;
        }
        if (
            window.location.pathname === '/mfa-enroll' ||
            window.location.hash.includes('#/mfa-enroll')
        ) {
            return;
        }

        const isInitialized = await getIsInitialized();

        if (!isInitialized) {
            await supabase.auth.signOut();
            cachedSale = undefined;
            getIsInitialized._is_initialized_cache = null;
            throw {
                redirectTo: '/sign-up',
                message: false,
            };
        }

        // Check base auth first - this handles login page and other auth logic
        await baseAuthProvider.checkAuth(params);

        // Additional check for user approval status - only if we have an authenticated user
        const {
            data: { user },
        } = await supabase.auth.getUser();

        let sale: Sale | null = null;
        if (user) {
            sale = await getSaleFromCache();

            if (sale && (sale as any).approved === false) {
                console.warn(
                    '[AuthProvider] checkAuth: User is not approved, signing out and redirecting to /login'
                );
                await supabase.auth.signOut();
                cachedSale = undefined;
                throw {
                    redirectTo: '/login',
                    message: 'Your account is pending administrator approval.',
                };
            }
        }

        // Enforce MFA verification if the user has already enrolled factors.
        const { data: aalData } =
            await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

        if (
            aalData &&
            aalData.currentLevel === 'aal1' &&
            aalData.nextLevel === 'aal2'
        ) {
            // Has factors enrolled but hasn't verified this session
            throw {
                redirectTo: '/mfa-verify',
                message: false,
            };
        }

        // Enforce MFA enrollment for users past their first sign-in
        if (
            aalData &&
            aalData.currentLevel === 'aal1' &&
            aalData.nextLevel === 'aal1' &&
            sale
        ) {
            const signInCount = (sale as any).sign_in_count ?? 0;
            if (signInCount > 1) {
                // Past first sign-in, no MFA enrolled — force enrollment
                throw {
                    redirectTo: '/mfa-enroll',
                    message: false,
                };
            }
        }

        return;
    },
    canAccess: async params => {
        const isInitialized = await getIsInitialized();
        if (!isInitialized) return false;
        const {
            data: { user },
        } = await supabase.auth.getUser();
        if (!user) return false;
        const sale = await getSaleFromCache();
        if (sale == null) return false;
        const role = sale.administrator ? 'admin' : 'user';
        return canAccess(role, params);
    },
    getIdentity: baseAuthProvider.getIdentity,
    registerUserAndProfile: async (data: Partial<Sale> & SignUpData) => {
        console.log(
            '[AuthProvider] registerUserAndProfile: Called with data:',
            data
        );
        const { email, password, first_name, last_name, ...profileData } = data;

        const { data: authSignupData, error: signUpError } =
            await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        first_name,
                        last_name,
                    },
                },
            });

        if (signUpError) {
            console.error(
                '[AuthProvider] registerUserAndProfile: supabase.auth.signUp error:',
                signUpError
            );
            throw signUpError;
        }
        if (!authSignupData.user) {
            console.error(
                '[AuthProvider] registerUserAndProfile: No user data returned from signUp'
            );
            throw new Error(
                'User registration failed: No user data returned from auth.'
            );
        }
        console.log(
            '[AuthProvider] registerUserAndProfile: auth.user created with ID:',
            authSignupData.user.id
        );

        const salesProfilePayload: Partial<Sale> = {
            ...profileData,
            user_id: authSignupData.user.id,
            email,
            first_name,
            last_name,
        };
        console.log(
            '[AuthProvider] registerUserAndProfile: Sales profile payload to create/update:',
            salesProfilePayload
        );

        try {
            let retries = 5;
            let salesRecordId: string | null = null;
            while (retries > 0 && !salesRecordId) {
                const { data: fetchedSale, error: fetchErr } = await supabase
                    .from('sales')
                    .select('id')
                    .eq('user_id', authSignupData.user.id)
                    .single();

                if (fetchErr && fetchErr.code !== 'PGRST116') {
                    console.warn(
                        `[AuthProvider] registerUserAndProfile: Attempt ${6 - retries} to fetch sales record failed, but not PGRST116:`,
                        fetchErr.message
                    );
                }

                if (fetchedSale) {
                    salesRecordId = fetchedSale.id;
                    console.log(
                        '[AuthProvider] registerUserAndProfile: Found existing sales record ID by trigger:',
                        salesRecordId
                    );
                    break;
                }
                console.log(
                    `[AuthProvider] registerUserAndProfile: Sales record (via trigger) not found for user ${authSignupData.user.id}, retrying (${retries - 1} left)...`
                );
                await new Promise(res => setTimeout(res, 750));
                retries--;
            }

            if (!salesRecordId) {
                console.error(
                    '[AuthProvider] registerUserAndProfile: Trigger did not create sales record for user_id after retries:',
                    authSignupData.user.id
                );
                throw new Error(
                    'Profile creation failed: Could not link profile to registered auth user.'
                );
            }

            console.log(
                `[AuthProvider] registerUserAndProfile: Calling dataProvider.salesUpdate for sales ID ${salesRecordId}`
            );
            await dataProvider.salesUpdate(
                salesRecordId,
                salesProfilePayload as Partial<Sale>
            );
            console.log(
                '[AuthProvider] registerUserAndProfile: salesUpdate call for profile data completed.'
            );

            // Sign out the user immediately after registration since new users are not approved by default
            console.log(
                '[AuthProvider] registerUserAndProfile: Signing out user after registration - approval required before login'
            );
            await supabase.auth.signOut();
            cachedSale = undefined;

            getIsInitialized._is_initialized_cache = true;
            return { ...authSignupData.user, ...salesProfilePayload };
        } catch (profileError: any) {
            console.error(
                '[AuthProvider] registerUserAndProfile: Error setting up sales profile:',
                profileError
            );
            // Sign out user even if profile setup fails to prevent unauthorized access
            await supabase.auth.signOut();
            cachedSale = undefined;
            const message =
                profileError instanceof Error
                    ? profileError.message
                    : String(profileError);
            throw new Error(
                `Registration succeeded but profile setup failed: ${message}. Please contact support.`
            );
        }
    },
};

let cachedSale: Sale | null | undefined = undefined;

const getSaleFromCache = async (): Promise<Sale | null> => {
    if (cachedSale !== undefined) {
        return cachedSale;
    }

    const { data: dataSession, error: errorSession } =
        await supabase.auth.getSession();

    // Shouldn't happen after login but just in case
    if (dataSession?.session?.user == null || errorSession) {
        cachedSale = null;
        return null;
    }

    const { data: dataSale, error: errorSale } = await supabase
        .from('sales')
        .select(
            'id, user_id, first_name, last_name, avatar, administrator, email, company_name, agent_number, company_logo, approved, sign_in_count'
        )
        .eq('user_id', dataSession.session.user.id)
        .single();

    if (errorSale) {
        console.error(
            `[AuthProvider] getSaleFromCache: Error fetching sales record for user ${dataSession.session.user.id}:`,
            errorSale
        );
        cachedSale = null;
        return null;
    }

    if (!dataSale) {
        cachedSale = null;
        return null;
    }

    cachedSale = dataSale as Sale;
    return dataSale as Sale;
};
